import { Priority, PriorityValue } from '../constants/const';

/** 炉数に応じて動的に要素数が変動する配列を表す */
class FurnacesCountDynamicArray<T> extends Array<T> {}

export interface ResultCarryVolumeContext {
  resultDate: string;
  carryVolume: number;
}

export interface FurnacesStatus {
  isShutdown: boolean;
  days: number;
  date: string | null;
  reference: 'result' | 'plan' | null;
}

/** (DraftConditionAPI GET) */

export class DraftConditionGetResponseObject {
  current: Date = new Date();
  furnacesCount = 0;
  pitLimit = 0;
  pitMax = 0;
  pitMin = 0;
  trashDensity = 0;
  status: FurnacesCountDynamicArray<FurnacesStatus> = [];
  trashPitRestContext: { trashPitRest: number | null; date: string | null; reference: 'result' | 'plan' | null } = { trashPitRest: null, date: null, reference: null };
  rateCapacity = 0;
  burnLoad: FurnacesCountDynamicArray<number> = [];
  resultCarryVolumes: ResultCarryVolumeContext[] = [];
  monthlyCarryCalculation: number[] = [];
  weeklyCarryCalculation: number[] = [];

  savedDraftCondition?: TempSavedCondition;
  ashRate = 0;
  truckMainAshCarryOut = 0;
  flyAshRate = 0;
  truckFlyAshCarryOut = 0;
}

type SettingMode = 'file' | 'calculation';

export class DraftConditionModel {
  branchId = 0;
  draftConditionStart? = '';
  draftConditionEnd? = '';
  pitLimit = 0;
  pitMax = 0;
  pitMin = 0;
  trashDensity = 0;
  ashRate = 0;
  truckMainAshCarryOut = 0;
  flyAshRate = 0;
  truckFlyAshCarryOut = 0;
  monthlyCarryCalculation: number[] = [];
  weeklyCarryCalculation: number[] = [];
  yearlyTrashCarryVolume = 0;
  planCarryVolume: number[] = [];
  minCarryVolume: number[] = [];
  referenceOfCarryVolume: number[] = [];
  minContinuousOperation: FurnacesCountDynamicArray<number> = [];
  shutdowns: FurnacesCountDynamicArray<number> = [];
  shutdownLaunchHour: number[] = [];
  shutdownBurnHours: number[] = [];
  shutdownElectricCreateHours: number[] = [];
  settingCsvPath: string = '';
}

export interface DraftShutdownModel {
  furnaces: number;
  shutdownDate: string;
  launchDate: string;
  launchDateFlag: number;
  shutdownDateFlag: number;
  notChangeFlag: boolean;
}

export class TempSavedCondition extends DraftConditionModel {
  shutdownDatePlans: FurnacesCountDynamicArray<DraftShutdownModel> = [];
  planCarryVolumeCsvRecords: [string, number][] = [];
}

const DEFAULT_MAX_CONTINUOUS_OPERATION = 140;
export class DraftConditionPostAPIParams {
  branchId = 0;
  userId = 0;
  draftConditionStart = '';
  draftConditionEnd = '';
  pitLimit = 0;
  pitMax = 0;
  pitMin = 0;
  trashDensity = 0;
  ashRate = 0;
  truckMainAshCarryOut = 0;
  flyAshRate = 0;
  truckFlyAshCarryOut = 0;
  untilFurnacesState: FurnacesCountDynamicArray<[number, number]> = []; // [停炉中フラグ, 日数]
  untilTrashPitRest = 0;
  yearlyTrashCarryVolume = 0;
  planCarryVolume = new Array(12).fill(0);
  minCarryVolume = new Array(12).fill(0);
  referenceOfCarryVolume = new Array(12).fill(0);
  prevPlanCarryVolume = new Array(12).fill(0);
  burnLoad: FurnacesCountDynamicArray<number> = [];
  planCarryVolumeCsvRecords: [string, number][] = [];
  commonShutdownDatePlan: [number, number, string, string, number, number] = [Date.now(), 1, '', '', 0, 0]; // [key, 炉, 立ち下げ日, 立ち上げ日, 変更不可フラグ（立ち下げ日）、変更不可フラグ（立ち上げ日）]
  shutdownDatePlans: FurnacesCountDynamicArray<[number, number, string, string, number, number]> = [[Date.now(), 1, '', '', 0, 0]]; // [key, 炉, 立ち下げ日, 立ち上げ日, 変更不可フラグ（立ち下げ日）、変更不可フラグ（立ち上げ日）]
  minContinuousOperation: FurnacesCountDynamicArray<number> = [];
  shutdowns: FurnacesCountDynamicArray<number> = [];
  settingCsvPath: string = '';
  monthlyCarryCalculation: number[] = new Array(12).fill(0);
  weeklyCarryCalculation: number[] = new Array(7).fill(0);
  shutdownLaunchHour: number[] = new Array(2).fill(0); // 立上下日の2日分
  shutdownBurnHours: number[] = new Array(2).fill(0); // 立上下日の2日分
  shutdownElectricCreateHours: number[] = new Array(2).fill(0); // 立上下日の2日分

  public constructor(furnacesCount?: number) {
    if (furnacesCount) {
      this.untilFurnacesState = new FurnacesCountDynamicArray<[number, number]>(furnacesCount).fill([0, 0]).map((_) => [1, 0]); // 別参照で[稼働, 0日]として初期化
      this.burnLoad = new FurnacesCountDynamicArray<number>(furnacesCount).fill(0);
      this.minContinuousOperation = new FurnacesCountDynamicArray<number>(furnacesCount).fill(0);
      this.shutdowns = new FurnacesCountDynamicArray<number>(furnacesCount).fill(0);
    }
  }
}

export class DraftConditionFormValuesObject extends DraftConditionPostAPIParams {
  settingMode: SettingMode = 'file';

  public constructor(furnacesCount?: number) {
    super(furnacesCount);
  }

  /** 炉数変更に伴う配列プロパティの初期化 */
  static setFurnacesCount(target: TempSavedCondition, furnacesCount: number, newFurnacesCount: number) {
    if (furnacesCount === newFurnacesCount) {
      return;
    }

    const newValue = new DraftConditionFormValuesObject(newFurnacesCount);
    // 炉数と配列の要素数が一致しているとは限らないので差分だけを追加・削除
    if (furnacesCount < newFurnacesCount) {
      // 要素追加
      furnacesCountDynamicArrayPropKeys.forEach((key) => {
        if ((target as any)[key]) {
          (target as any)[key] = (target as any)[key].concat((newValue as any)[key].slice((target as any)[key].length));
        }
      });
    } else if (furnacesCount > newFurnacesCount) {
      // 要素削除
      furnacesCountDynamicArrayPropKeys.forEach((key) => {
        if ((target as any)[key]) {
          (target as any)[key] = (target as any)[key].slice(0, (newValue as any)[key].length);
        }
      });
    }
  }

  public static restoreTempSavedCondition({
    tempSavedCondition,
    responseFurnacesCount,
    furnacesCount,
    isInitialized,
  }: {
    tempSavedCondition: TempSavedCondition;
    responseFurnacesCount: number;
    furnacesCount: number;
    isInitialized: boolean;
  }) {
    if (!tempSavedCondition) {
      return {};
    }

    if (isInitialized) {
      // 初期表示済みなら期間の値は復元しない
      tempSavedCondition.draftConditionStart = undefined;
      tempSavedCondition.draftConditionEnd = undefined;
    }

    console.log('tempSavedCondition', tempSavedCondition);

    // 一時保存データの炉数合わせ
    if (responseFurnacesCount) {
      DraftConditionFormValuesObject.setFurnacesCount(tempSavedCondition, responseFurnacesCount, furnacesCount);
    }

    const formValues = {
      furnacesCount,
      settingMode: 'file' as SettingMode,
    } as any;
    Object.keys(tempSavedCondition).forEach((key) => {
      const value = (tempSavedCondition as any)[key];
      formValues[key] = value;
    });

    const commonShutdownPlan = tempSavedCondition.shutdownDatePlans.find((plan) => plan.notChangeFlag);
    console.log('commonShutdownPlan', commonShutdownPlan);
    if (commonShutdownPlan) {
      formValues.commonShutdownDatePlan = [
        Date.now(),
        0,
        commonShutdownPlan.shutdownDate,
        commonShutdownPlan.launchDate,
        commonShutdownPlan.shutdownDateFlag,
        commonShutdownPlan.launchDateFlag,
      ];
    }

    formValues.shutdownDatePlans = tempSavedCondition.shutdownDatePlans
      .filter((plan) => !plan.notChangeFlag)
      .map((plan, i) => [i, i + 1, plan.shutdownDate, plan.launchDate, plan.shutdownDateFlag, plan.launchDateFlag]);

    Object.keys(formValues).forEach((key) => {
      const value = (formValues as any)[key];
      // falsyまたは空配列の値は削除 (マージ時上書きの回避)
      if (!value || (Array.isArray(value) && !value.length)) {
        delete (formValues as any)[key];
      }
    });

    console.log('restoredConditions', formValues);

    return formValues;
  }
}

export const DraftConditionGetResponseIndexMap = Object.keys(new DraftConditionGetResponseObject()).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });

export const DraftConditionFormIndexMap = Object.keys(new DraftConditionFormValuesObject()).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });

const furnacesCountDynamicArrayPropKeys = (() => {
  const obj = new DraftConditionFormValuesObject(1);
  return Object.keys(obj).filter((key) => (obj as any)[key] instanceof FurnacesCountDynamicArray);
})();

export const divideCarryVolumeIntoMonthly = (items: [string, number][], draftConditionStart: string) => {
  const dateIndex = 0;
  const valueIndex = 1;

  // 年度はじめと先月末を算出
  const draftConditionStartDate = new Date(draftConditionStart);
  const monthIndex = draftConditionStartDate.getMonth();
  const year = draftConditionStartDate.getFullYear();
  const startYear = monthIndex + 1 < 4 ? year - 1 : year;

  const startDate = new Date(startYear, 3, 1); // 年度はじめを取得
  const endDate = new Date(startYear + 1, 2, 31); // 年度末を取得

  // 各月の合計を出す（4月を起点にインデックスにマップ）
  const resultArray = new Array(12).fill(0);

  items.forEach((item) => {
    const dateStr = item[dateIndex];
    if (dateStr) {
      const d = new Date(dateStr);
      if (d >= startDate && d <= endDate) {
        const relativeIndex = (d.getMonth() + 9) % 12; // 4月を0としたインデックス
        if (item[valueIndex]) {
          resultArray[relativeIndex] += item[valueIndex];
        }
      }
    }
  });
  return resultArray;
};
